import React, { useState } from 'react';
import { NavLink, Outlet, Navigate, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, BookOpen, Notebook, Users, User, Award, Settings,
  Menu, X, LogOut, GraduationCap, ShieldCheck, Download, MessageCircle,
} from 'lucide-react';
import { usePortalAuth } from '../../contexts/PortalAuthContext';
import usePWAInstall from '../../hooks/usePWAInstall';

const navItems = [
  { to: '/portal',           icon: LayoutDashboard, label: 'Dashboard',  exact: true },
  { to: '/portal/modules',   icon: BookOpen,        label: 'Modules' },
  { to: '/portal/community', icon: MessageCircle,   label: 'Community' },
  { to: '/portal/journal',   icon: Notebook,        label: 'Journal' },
  { to: '/portal/cohort',    icon: Users,           label: 'Members' },
  { to: '/portal/certificate', icon: Award,         label: 'Certificate' },
  { to: '/portal/profile',   icon: User,            label: 'My Profile' },
];

const adminItems = [
  { to: '/portal/admin',          icon: ShieldCheck, label: 'Admin Panel' },
];

export default function PortalLayout() {
  const { user, loading, logout } = usePortalAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { installable, install, isInstalled } = usePWAInstall();

  if (loading || user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 text-slate-200" data-testid="portal-loading">
        <div className="animate-pulse text-sm tracking-wider">LOADING PORTAL…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/portal/login" replace />;

  const handleLogout = async () => {
    await logout();
    navigate('/portal/login');
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex">
      {/* Mobile toggle */}
      <button
        onClick={() => setOpen(!open)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 rounded-lg bg-slate-800/90 backdrop-blur border border-slate-700"
        data-testid="portal-menu-toggle"
        aria-label="Toggle menu"
      >
        {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </button>

      {/* Sidebar */}
      <aside
        className={`fixed lg:sticky top-0 left-0 h-screen w-72 bg-slate-900/95 backdrop-blur border-r border-slate-800
          flex flex-col z-40 transition-transform duration-300
          ${open ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}
      >
        <div className="px-6 py-6 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
              <GraduationCap className="w-6 h-6 text-slate-950" />
            </div>
            <div>
              <div className="text-sm font-semibold tracking-wide">AUDIO &amp; CO</div>
              <div className="text-xs text-slate-400">Training Portal</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {navItems.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.exact}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition
                ${isActive
                  ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                  : 'text-slate-300 hover:bg-slate-800 hover:text-white'}`
              }
              data-testid={`portal-nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
            >
              <item.icon className="w-4 h-4" />
              {item.label}
            </NavLink>
          ))}

          {user.role === 'admin' && (
            <>
              <div className="px-3 pt-6 pb-2 text-xs uppercase tracking-wider text-slate-500">Admin</div>
              {adminItems.map(item => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition
                    ${isActive
                      ? 'bg-rose-500/10 text-rose-400 border border-rose-500/20'
                      : 'text-slate-300 hover:bg-slate-800 hover:text-white'}`
                  }
                  data-testid={`portal-nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                >
                  <item.icon className="w-4 h-4" />
                  {item.label}
                </NavLink>
              ))}
            </>
          )}
        </nav>

        <div className="px-3 py-4 border-t border-slate-800">
          {installable && !isInstalled && (
            <button
              onClick={install}
              className="w-full flex items-center gap-2 px-3 py-2 mb-2 rounded-lg text-sm bg-amber-500/10 text-amber-400 border border-amber-500/20 hover:bg-amber-500/20 transition"
              data-testid="portal-install-btn"
            >
              <Download className="w-4 h-4" /> Install as app
            </button>
          )}
          <div className="flex items-center gap-3 px-3 py-2 mb-2">
            {user.avatar_url ? (
              <img src={user.avatar_url} alt="" className="w-8 h-8 rounded-full object-cover" />
            ) : (
              <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center text-xs font-semibold">
                {(user.full_name || 'U').slice(0,1).toUpperCase()}
              </div>
            )}
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">{user.full_name}</div>
              <div className="text-xs text-slate-400 truncate">{user.email}</div>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-300 hover:bg-slate-800 hover:text-white"
            data-testid="portal-logout-btn"
          >
            <LogOut className="w-4 h-4" /> Log out
          </button>
        </div>
      </aside>

      {/* Backdrop on mobile */}
      {open && (
        <div className="lg:hidden fixed inset-0 bg-black/60 z-30" onClick={() => setOpen(false)} />
      )}

      {/* Main */}
      <main className="flex-1 min-w-0">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8 lg:py-12">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
